<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { Home, LayoutGrid, Plus, Heart, User } from 'lucide-vue-next'
import { useUserStore } from '@/stores/user'
import { useI18n } from '@/composables/useI18n'

const route = useRoute()
const userStore = useUserStore()
const { t } = useI18n()

function isTabActive(path: string, exact = false) {
  if (exact) return route.path === path
  return route.path.startsWith(path)
}

// A logged-out visitor who taps the profile tab lands on an auth screen, so the
// whole auth cluster counts as "account" for the current-tab indicator. Without
// this the bar showed no active tab at all on /login and friends.
const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email']

const isHomeTab = computed(() => isTabActive('/', true))
const isCategoriesTab = computed(() => isTabActive('/categories'))
const isSellTab = computed(() => isTabActive('/sell'))
const isWatchlistTab = computed(() => route.path.includes('watchlist'))
const isAccountTab = computed(
  () =>
    (route.path.startsWith('/dashboard') && !isWatchlistTab.value) ||
    (!userStore.isAuthenticated && AUTH_PATHS.some((path) => isTabActive(path))),
)

// Logged out, the watchlist tab still has to route through the login screen —
// but it carries its own redirect so it is no longer the same destination as
// the profile tab, and the visitor lands where they meant to go.
const watchlistTarget = computed(() =>
  userStore.isAuthenticated
    ? '/dashboard/watchlist'
    : { path: '/login', query: { redirect: '/dashboard/watchlist' } },
)
const accountTarget = computed(() => (userStore.isAuthenticated ? '/dashboard/overview' : '/login'))

const tabClass = (active: boolean) =>
  active ? 'text-ink' : 'text-text-muted hover:text-text-secondary'

const tabBase =
  'flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl py-1 text-[11px] font-semibold transition select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
</script>

<template>
  <nav
    class="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-6px_24px_rgba(20,23,31,0.06)] backdrop-blur-xl lg:hidden"
    :aria-label="t('nav.navigation')"
  >
    <div class="grid h-16 grid-cols-5 items-center px-1">
      <RouterLink
        to="/"
        :class="[tabBase, tabClass(isHomeTab)]"
        :aria-current="isHomeTab ? 'page' : undefined"
      >
        <Home class="h-5 w-5" :class="isHomeTab ? 'stroke-[2.5]' : 'stroke-[1.75]'" />
        <span class="w-full truncate text-center">{{ t('nav.home') }}</span>
        <span class="h-1 w-1 rounded-full" :class="isHomeTab ? 'bg-primary' : 'bg-transparent'" />
      </RouterLink>

      <RouterLink
        to="/categories"
        :class="[tabBase, tabClass(isCategoriesTab)]"
        :aria-current="isCategoriesTab ? 'page' : undefined"
      >
        <LayoutGrid class="h-5 w-5" :class="isCategoriesTab ? 'stroke-[2.5]' : 'stroke-[1.75]'" />
        <span class="w-full truncate text-center">{{ t('nav.categories') }}</span>
        <span class="h-1 w-1 rounded-full" :class="isCategoriesTab ? 'bg-primary' : 'bg-transparent'" />
      </RouterLink>

      <RouterLink
        to="/sell"
        class="group relative flex min-h-[44px] min-w-0 flex-col items-center justify-center rounded-xl select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        :aria-current="isSellTab ? 'page' : undefined"
      >
        <span class="absolute -top-5 flex h-12 w-12 items-center justify-center rounded-full border-4 border-surface bg-primary text-onPrimary shadow-[0_8px_20px_-6px_rgb(var(--color-primary)/0.7)] transition-transform group-active:scale-90">
          <Plus class="h-5 w-5 stroke-[3]" />
        </span>
        <span class="mt-6 w-full truncate text-center text-[11px] font-bold text-ink">{{ t('nav.sell') }}</span>
      </RouterLink>

      <RouterLink
        :to="watchlistTarget"
        :class="[tabBase, tabClass(isWatchlistTab)]"
        :aria-current="isWatchlistTab ? 'page' : undefined"
      >
        <Heart class="h-5 w-5" :class="isWatchlistTab ? 'fill-ink stroke-[2.5]' : 'stroke-[1.75]'" />
        <span class="w-full truncate text-center">{{ t('dashboard.watchlist') }}</span>
        <span class="h-1 w-1 rounded-full" :class="isWatchlistTab ? 'bg-primary' : 'bg-transparent'" />
      </RouterLink>

      <RouterLink
        :to="accountTarget"
        :class="[tabBase, tabClass(isAccountTab)]"
        :aria-current="isAccountTab ? 'page' : undefined"
      >
        <User class="h-5 w-5" :class="isAccountTab ? 'stroke-[2.5]' : 'stroke-[1.75]'" />
        <span class="w-full truncate text-center">{{ userStore.isAuthenticated ? t('nav.myProfile') : t('nav.login') }}</span>
        <span class="h-1 w-1 rounded-full" :class="isAccountTab ? 'bg-primary' : 'bg-transparent'" />
      </RouterLink>
    </div>
  </nav>
</template>
